import { useTranslation } from 'react-i18next';

import { Button, Skeleton } from '@ctct/payflow-ui';
import { Alert, Col, Row } from '@ctct/presentation-layer';

import { CreditCardSavedPaymentMethod } from './CreditCardSavedPaymentMethod';
import { useSecurePay } from './useSecurePay';
import type { PaymentMethodsLoadedProps } from '../widgets/PaymentMethodsWidget/types';

interface Props {
  data: Pick<
    PaymentMethodsLoadedProps['data'],
    | 'getCreditCardName'
    | 'getCreditCardNumberFormatted'
    | 'getCreditCardExpirationDate'
    | 'getCreditCardLogoName'
    | 'getCreditCardType'
    | 'getSecurePayCreditCardURL'
    | 'getSecurePayIframeId'
  >;
  handlers: Pick<
    PaymentMethodsLoadedProps['handlers'],
    | 'onStartListeningSecurePay'
    | 'onStopListeningSecurePay'
    | 'onSecurePayEditClick'
    | 'onSecurePayCancelEditClick'
    | 'onSecurePayIframeLoad'
    | 'onSecurePayIframeError'
    | 'clearTimeoutIds'
  >;
  hooks: Pick<PaymentMethodsLoadedProps['hooks'], 'useSecurePayIframeState' | 'useTabsLoading' | 'useFullLoading'>;
}

export const CreditCard = ({ data, handlers, hooks }: Props) => {
  const { t } = useTranslation();

  const { addedCssToIframe, creditCardConnected, securePayEdited, showSecurePayIframeBlockedMessage } =
    hooks.useSecurePayIframeState();

  const tabsLoading = hooks.useTabsLoading();
  const isFullLoading = hooks.useFullLoading();

  const isLoading = tabsLoading || isFullLoading;

  useSecurePay(handlers);

  if (creditCardConnected && !securePayEdited) {
    return (
      <CreditCardSavedPaymentMethod
        creditCardBrandDisplayName={data.getCreditCardName()}
        creditCardType={data.getCreditCardType()}
        formattedCardNumber={data.getCreditCardNumberFormatted()}
        expirationDate={data.getCreditCardExpirationDate()}
        logoName={data.getCreditCardLogoName()}
        actions={
          !isLoading && (
            <Button
              className="edit-credit-card-button"
              data-qe-id="edit-cc"
              variant="text"
              onClick={handlers.onSecurePayEditClick}
            >
              {t('payment-methods.edit')}
            </Button>
          )
        }
      />
    );
  }

  if (showSecurePayIframeBlockedMessage) {
    return <Alert variant="warning" message={t('payment-methods.credit_card_loading_error')} />;
  }

  return (
    <div className="credit-card-wrapper" style={{ position: 'relative' }}>
      <Row>
        <Col>
          {addedCssToIframe ? null : <Skeleton height={320} />}
          {creditCardConnected && (
            <Button
              variant="text"
              className="cancel-edit-credit-card-button"
              data-qe-id="cancel-edit-cc"
              onClick={handlers.onSecurePayCancelEditClick}
              busy={tabsLoading}
              disabled={tabsLoading}
            >
              {t('payment-methods.cancel')}
            </Button>
          )}
        </Col>
        <iframe
          src={data.getSecurePayCreditCardURL()}
          frameBorder={0}
          onLoad={handlers.onSecurePayIframeLoad}
          onError={handlers.onSecurePayIframeError}
          data-qe-id="secure-pay-iframe"
          id={data.getSecurePayIframeId()}
          className="cc-iframe"
          title="cc"
          loading="eager"
          style={
            // previously we used display: none; to hide iframe but it could cause issues with iframe loading, so now we use opacity and position to achieve the same effect
            addedCssToIframe
              ? {
                  opacity: 1,
                  position: 'static',
                  top: 'auto',
                  left: 'auto',
                }
              : {
                  opacity: 0,
                  position: 'absolute',
                  top: '0',
                  left: '0',
                }
          }
        />
      </Row>
    </div>
  );
};
